import React, { useMemo, useState } from "react";
import { useGetProgramCompareQuery, useUpdateStarPayDateMutation } from "../api";
import { UserAutocomplete } from "../components/UserAutocomplete";
import { useNotification } from "../hooks/useNotification";

const ProgramComparePage: React.FC = () => {
    const { showNotification } = useNotification();
    const [page, setPage] = useState(1);
    const perPage = 50;

    const [userQuery, setUserQuery] = useState("");
    const selectedUserId = useMemo(() => {
        const trimmed = userQuery.trim();
        if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
        return undefined;
    }, [userQuery]);

    const [sortBy, setSortBy] = useState<
        "diff_days" | "member_fee_at" | "star_pay_at" | "user_id"
    >("diff_days");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    const { data, isLoading, isFetching } = useGetProgramCompareQuery({
        page,
        per_page: perPage,
        user_id: selectedUserId,
        sort_by: sortBy,
        sort_order: sortOrder,
    });
    const [updateStarPayDate, { isLoading: isUpdatingStarPayDate }] =
        useUpdateStarPayDateMutation();

    const items = data?.data || [];
    const meta = data?.meta;
    const stats = data?.stats;

    const formatDate = (value: string | null | undefined) => {
        if (!value) return "—";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString("ru-RU");
    };

    const canPrev = meta ? meta.current_page > 1 : false;
    const canNext = meta ? meta.current_page < meta.last_page : false;

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    const [editingUserName, setEditingUserName] = useState<string>("");
    const [editStarPayAt, setEditStarPayAt] = useState<string>("");

    const openEditModal = (userId: number, displayName: string, current: string | null) => {
        setEditingUserId(userId);
        setEditingUserName(displayName);
        setEditStarPayAt(toDatetimeLocalValue(current));
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingUserId(null);
        setEditingUserName("");
        setEditStarPayAt("");
    };

    const toDatetimeLocalValue = (value: string | null): string => {
        if (!value) return "";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "";

        const pad = (n: number) => n.toString().padStart(2, "0");
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const mi = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    };

    const toBackendDateTime = (value: string): string => {
        // datetime-local => "YYYY-MM-DDTHH:mm"
        return `${value.replace("T", " ")}:00`;
    };

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">
                    Сравнение дат оплат (Структурная vs Звёздная)
                </h1>
                {isFetching && (
                    <span className="text-sm text-gray-500">Обновление...</span>
                )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <div className="text-sm text-gray-500 mb-1">
                            Пользователь (ID / email / телефон)
                        </div>
                        <UserAutocomplete
                            value={userQuery}
                            onChange={(v) => {
                                setUserQuery(v);
                                setPage(1);
                            }}
                            placeholder="Начните вводить..."
                        />
                        {selectedUserId && (
                            <button
                                type="button"
                                className="mt-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                onClick={() => {
                                    setUserQuery("");
                                    setPage(1);
                                }}
                            >
                                Сбросить фильтр
                            </button>
                        )}
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Сортировка</div>
                        <select
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value={sortBy}
                            onChange={(e) => {
                                setSortBy(
                                    e.target.value as
                                        | "diff_days"
                                        | "member_fee_at"
                                        | "star_pay_at"
                                        | "user_id"
                                );
                                setPage(1);
                            }}
                        >
                            <option value="diff_days">Δ дней (abs)</option>
                            <option value="member_fee_at">Структурная</option>
                            <option value="star_pay_at">Звёздная</option>
                            <option value="user_id">ID</option>
                        </select>
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Порядок</div>
                        <select
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value={sortOrder}
                            onChange={(e) => {
                                setSortOrder(e.target.value as "asc" | "desc");
                                setPage(1);
                            }}
                        >
                            <option value="desc">По убыванию</option>
                            <option value="asc">По возрастанию</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="text-sm text-gray-500">Совпадают (≤5 дней)</div>
                    <div className="text-2xl font-semibold">
                        {stats ? stats.ok_aligned : "—"}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="text-sm text-gray-500">Расхождения (больше 5 дней)</div>
                    <div className="text-2xl font-semibold">
                        {stats ? stats.misaligned_gt5 : "—"}
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ID
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Имя
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Структурная
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Звёздная
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Δ дней
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Действия
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {isLoading ? (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-4 py-6 text-center text-sm text-gray-500"
                                >
                                    Загрузка...
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-4 py-6 text-center text-sm text-gray-500"
                                >
                                    Нет данных
                                </td>
                            </tr>
                        ) : (
                            items.map((item) => {
                                const diff = item.diff_days;
                                const highlight =
                                    diff !== null && Math.abs(diff) > 5
                                        ? "text-red-600 dark:text-red-400"
                                        : "text-gray-900 dark:text-gray-100";
                                return (
                                    <tr key={item.user_id}>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {item.user_id}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {item.name || ""} {item.surname || ""}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {formatDate(item.member_fee_at)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {formatDate(item.star_pay_at)}
                                        </td>
                                        <td className={`px-4 py-3 text-sm ${highlight}`}>
                                            {diff === null ? "—" : diff}
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm">
                                            <button
                                                className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                                                title="Изменить дату оплаты Звёздной"
                                                disabled={isUpdatingStarPayDate}
                                                onClick={() =>
                                                    openEditModal(
                                                        item.user_id,
                                                        `${item.name || ""} ${item.surname || ""}`.trim(),
                                                        item.star_pay_at
                                                    )
                                                }
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                    />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                    Страница {meta?.current_page || 1} из {meta?.last_page || 1} • Всего{" "}
                    {meta?.total || 0}
                </div>
                <div className="space-x-2">
                    <button
                        className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                        onClick={() => canPrev && setPage((p) => p - 1)}
                        disabled={!canPrev}
                    >
                        Назад
                    </button>
                    <button
                        className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                        onClick={() => canNext && setPage((p) => p + 1)}
                        disabled={!canNext}
                    >
                        Вперёд
                    </button>
                </div>
            </div>

            {isEditModalOpen && editingUserId !== null && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Дата оплаты Звёздной
                            </h2>
                            <button
                                onClick={closeEditModal}
                                className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="text-sm text-gray-500 mb-4">
                            Пользователь: <span className="text-gray-900 dark:text-gray-100">#{editingUserId}</span>{" "}
                            {editingUserName ? `(${editingUserName})` : ""}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-500 mb-1">
                                    user_pay_date
                                </label>
                                <input
                                    type="datetime-local"
                                    value={editStarPayAt}
                                    onChange={(e) => setEditStarPayAt(e.target.value)}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <div className="mt-2">
                                    <button
                                        type="button"
                                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                        onClick={() => setEditStarPayAt("")}
                                    >
                                        Очистить дату
                                    </button>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
                                    onClick={closeEditModal}
                                    disabled={isUpdatingStarPayDate}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="button"
                                    className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                                    disabled={isUpdatingStarPayDate}
                                    onClick={async () => {
                                        try {
                                            await updateStarPayDate({
                                                userId: editingUserId,
                                                user_pay_date:
                                                    editStarPayAt === ""
                                                        ? null
                                                        : toBackendDateTime(editStarPayAt),
                                            }).unwrap();
                                            showNotification("Дата обновлена", "success");
                                            closeEditModal();
                                        } catch (e: any) {
                                            showNotification(
                                                e?.data?.message || "Ошибка при обновлении даты",
                                                "error"
                                            );
                                        }
                                    }}
                                >
                                    Сохранить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgramComparePage;

